import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { DEBUG } from 'app/auth/authRoles'
import { ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { joinWithDifferentLastSeparator } from 'utilsTS'
import { SalesforceFieldDescribe } from './components/formComponentsDefinitions'
import { getFormPicklistOptions } from './components/picklist/formPicklistUtils'
import { formConditionToText } from './pdf-components/formPdfUtils'
import { ConditionData } from './types/Conditions'
import { ConnectedMap, ElementsMap } from './types/FormTypes'

export const getObjectsIdsFromConnectedMap = (connectedMap: ConnectedMap, mainConnectedObject: string): {
  connectedAccount: string
  connectedSurvey: string
  connectedOpportunity: string
  editedObjectId: string
  connectedResource: string
} => {
  let connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId

  Object.values(connectedMap).forEach((obj) => {
    if (obj.objectType === 'Opportunity') {
      connectedOpportunity = obj.sfObject.Id
    }
    if (obj.objectType === 'Account') {
      connectedAccount = obj.sfObject.Id
    }
    if (obj.objectType === 'Resource__c') {
      connectedResource = obj.sfObject.Id
    }
    if (obj.objectType === 'Survey__c') {
      connectedSurvey = obj.sfObject.Id
    }
    if (obj.objectType === mainConnectedObject) {
      editedObjectId = obj.sfObject.Id
    }
  })
  return { connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId }
}

export function shouldBlockAutoSaveForUser (user: ReduxUser): boolean {
  const isTesting = sfOauthConfig.isTesting as boolean
  const isDebug = user.role.includes(DEBUG)
  return !isTesting && isDebug
}

export const getPDFLabelFromConditions = ({
  elementsMap,
  type,
  connectedFieldDetails,
  idToRenderIndex,
  langVersion,
  conditions = []
}: {
  conditions: ConditionData[]
  type: string
  langVersion: 'en' | 'fr' | 'iu-cans'
  elementsMap: ElementsMap
  idToRenderIndex: Record<string, number>
  connectedFieldDetails: SalesforceFieldDescribe
}): string[] => {
  // This function parses single condition to text
  const transformConditionToText = (condition): string => {
    const elementType = elementsMap[condition.conditionTarget].elementType
    let parameter = condition.parameter
    if (elementType === 'picklist') {
      const picklistOptions = getFormPicklistOptions({
        connectedFieldDetails,
        typeProps: elementsMap[condition.conditionTarget].typeProps,
        langVersion,
        i18n
      })
      if (!Array.isArray(condition.parameter)) {
        const picklistOption = picklistOptions.find(
          (option, index) => `option${index}` === condition.parameter || option.apiValue === condition.parameter
        )
        parameter = picklistOption?.title
      } else {
        const toSet: string[] = []
        condition.parameter.forEach((param, index) => {
          const picklistOption = picklistOptions.find(
            (option) =>
              option.apiValue === param || `option${String(index)}` === param
          )
          toSet.push(picklistOption?.title ?? '')
        })
        parameter = joinWithDifferentLastSeparator(toSet, ', ', ` ${i18n._(t`OR`)} `)
      }
    }
    return formConditionToText({
      ...condition,
      parameter,
      affectedElementType: type,
      elementType,
      targetIndex: idToRenderIndex[condition.conditionTarget]
    })
  }

  const isTargetedCondition = (condition: ConditionData, state: string): boolean =>
    condition.conditionTarget != null &&
  elementsMap[condition.conditionTarget] != null &&
  ['hide', 'show', 'required', 'notRequired'].includes(state)

  return conditions
    .filter((condition) => {
      // We are displaying only conditions relating to visibility or elements being required as only those are relevant
      return Array.isArray(condition.conditions) ? condition.conditions.some(sub => isTargetedCondition(sub, condition.state)) : isTargetedCondition(condition, condition.state)
    })
    .map((condition) => {
      const { state, affectedElementType, logic } = condition

      // If conditions is a parent condition
      if (Array.isArray(condition.conditions)) {
        const joinString = logic === 'any' ? i18n._(t`OR`) : i18n._(t`AND`)
        const multipleCondtionsPerTarget = {}

        // It makes no sense to list conditions for the same target separately, so we are grouping into one
        // those that relate to the same form element, we just list options joined
        condition.conditions.filter((condition) => {
          return (
            condition.conditionTarget != null && elementsMap[condition.conditionTarget]
          )
        }).forEach(subCondition => {
          if (subCondition.conditionTarget != null) {
            const existingData = multipleCondtionsPerTarget[subCondition.conditionTarget]
            if (existingData == null) {
              subCondition.state = 'sub'
              multipleCondtionsPerTarget[subCondition.conditionTarget] = subCondition
            } else {
              if (!Array.isArray(existingData.parameter)) {
                existingData.parameter = [existingData.parameter]
              } else if (existingData.parameter.includes(subCondition.parameter) == null) {
                multipleCondtionsPerTarget[subCondition.conditionTarget].parameter.push(subCondition.parameter)
              }
            }
          }
        })

        const map = Object.values(multipleCondtionsPerTarget).map(conditionData => transformConditionToText(conditionData))

        let toRet = ''
        if (state === 'show' || state === 'required') {
          toRet = affectedElementType === 'uploadFiles'
            ? String(i18n._(t`PDF_CONDITION_IF`)) + ' '
            : String(i18n._(t`PDF_CONDITION_ANSWER_IF`)) + ' '
        } else if (state === 'hide' || state === 'notRequired') {
          return String(i18n._(t`PDF_CONDITION_DO_NOT_ANSWER_IF`)) + ' '
        }
        return toRet + map.join(` ${joinString} `)
      } else {
        return transformConditionToText(condition)
      }
    })
    .filter((v) => v)
}

/**
 * Checks complex Salesforce save results for error occurrences.
 * @param result The save result(s) to inspect.
 * @returns True if any error is found.
 */
export const checkIfSavingResultHasErrors = (result: any): boolean => {
  if (Array.isArray(result)) {
    return result.some((sub) => checkIfSavingResultHasErrors(sub))
  } else {
    if (result == null) {
      console.error(
        'No promise resolution configured or an unexpected error ocurred!'
      )
      return true
    }
    if (result.compositeResponse != null) {
      return result.compositeResponse.some((comp) => {
        if (comp.body == null) {
          return false
        } else {
          return comp.body.some((sub) => sub.success === false)
        }
      })
    }
    if ('isSuccess' in result) {
      if (result.outputValues != null && 'error' in result.outputValues) {
        return Boolean(result.outputValues.error)
      }
      return result.isSuccess === false
    }
    if ('success' in result) {
      return result.success === false
    }
    if ('hasErrors' in result) {
      return result.hasErrors
    }
    return false
  }
}
